<template>
  <div class='wrapper-page'>
    <contact-form ref='contactForm' @success='loadContact()' />

    <div id='contact-details' class='container-fluid'>
      <h1 class='page-title'>
        {{ contact.name }}

        <a @click.prevent='$refs.contactForm.open(contact)' href='#' class='edit'>
          {{$t('contacts.show.edit')}}
        </a>
      </h1>

      <div class='contact-info'>
        <div class="company-details">
          <h5>
            {{ $t('contacts.show.company') }}
            <strong v-if='contact.company'>
              {{ contact.company }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.position') }}
            <strong v-if='contact.position'>
              {{ contact.position }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_position_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.company_address') }}
            <strong v-if='contact.address'>
              {{ contact.address }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_address_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.expired_files_link') }}

            <div class="copy-to-clipboard-wrapper">
              <input type="text" name="public_link" id="public_link" :value="contactPageUrl" disabled="disabled" class="public_link input-link-to-clipboard">

              <a href="" class="btn btn-outline-primary float-right copy-link-to-clipboard" @click.prevent="copyToClipboard($event, '.input-link-to-clipboard')">
                <i class="fas fa-link"></i>
                {{ $t('folder_show.settings.upload_link_button') }}
              </a>
            </div>
          </h5>
        </div>
        <div class="contact-details">
          <h5>
            {{ $t('contacts.show.email') }}
            <strong v-if='contact.email'>
              {{ contact.email }}
            </strong>
            <strong class='red-text' v-else>
              {{ $t('contacts.show.no_email') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.language') }}
            <strong v-if="contact.locale === 'de'">
              {{ $t('contacts.edit_modal.german') }}
            </strong>
            <strong v-else>
              {{ $t('contacts.edit_modal.english') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.notes') }}
            <strong v-if='contact.notes'>
              {{ contact.notes }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_notes_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.communication_status') }}
            <router-link
              :data-tooltip='lastMessageAlt'
              :to="{name: 'contact_communications', params: { id: id }}"
              class='own-tooltip own-tooltip-right'>
              <img :src='lastMessageSrc'/>
            </router-link>
          </h5>
          <h5>
            {{ $t('contacts.show.files') }}
            <strong>{{ contactStats.records_count }}</strong>
          </h5>
        </div>

        <div class='contact-tags'>
          <h5 class='tags-dl'>
            {{ $t('contacts.show.tags') }}
          </h5>

          <div class='tags-cont'>
            <tag-manager :resource='contact' resourceType='contact'
                         v-on:reload-list='loadContact()'/>

            <div :class='tag.color' class='existing-tags-wrapper'
                 v-for='tag in contact.tags'>
              <div class='btn-group btn-group-toggle' data-toggle='buttons'>
                <label class='btn active'>
                  <router-link :to="{name: 'tag', params: { id: tag.id }}">
                    {{ tag.name }}
                  </router-link>
                </label>

                <label @click.prevent='removeConnectedTag(tag)' class='btn'>
                  <i class='fa fa-remove'></i>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="float-left checklist-status">
          <h5>
            {{ $t('contacts.show.checklist_status') }}
          </h5>
          <checklist-status :completed-items-count='contactStats.checked_checklist_items' :items-count='contactStats.total_checklist_items'/>
        </div>
      </div>

      <div class='nav-tabs-wrapper'>
        <ul role='tablist' class='nav nav-tabs'>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_folders', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.folders')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_communications', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.communication')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_interactive_notes', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.interactive_notes')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_emails', params: { id: id } }" class="nav-link show active">
              {{$t('contacts.show.automated_emails')}}
            </router-link>
          </li>
        </ul>
      </div>

      <div class='tab-content'>
        <div class="tab-pane active text-center">
          <loading v-if="loading" />
          <div class="empty-list-wrapper" v-else-if="!contact && !loading">
            <p>{{$t('contacts.show.no_automated_emails')}}</p>
          </div>
          <automated-emails :contact-id='contact.id' :contact-name='contact.name' v-else />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import _modal from '../components/_modal'
  import _loading from '../components/_loading'
  import _tag_manager from '../components/tags/_tags_manager'
  import _checklist_status from '../components/contacts/_checklist_status'
  import _automated_emails from '../components/contacts/_automated_emails'
  import _contact_form from '../components/shared/contacts/_contact_form'

  export default {
    components: {
      modal: _modal,
      'tag-manager': _tag_manager,
      'contact-form': _contact_form,
      'automated-emails': _automated_emails,
      'checklist-status': _checklist_status,
      'loading': _loading,
    },
    props: ['id'],
    created() {
      this.loadContact()
      this.loadFolders()
      this.store.sendMetrics({name: 'contact_emails', params: this.$route.params, path: this.$route.fullPath})
    },
    watch: {
      id() {
        this.loadContact()
        this.loadFolders()
      },
    },
    data() {
      return {
        modalContact: {},
        loading: true,
      }
    },
    computed: {
      contacts() {
        return this.lists.contacts || []
      },
      contactStats () {
        if (!this.stats.contacts)
          return {}

        return this.stats.contacts[0]
      },
      folderStats() {
        if (!this.stats.folders)
          return {}

        const stats = {}
        this.stats.folders.forEach(f => stats[f.id] = f)
        return stats
      },
      contact() {
        return this.state.contact || {}
      },
      inboxes() {
        return this.contact.inboxes || []
      },
      lastMessage() {
        const documentRequests = this.inboxes.filter(i => i && i.interaction_type === 'DocumentRequest').map(i => i.interaction)

        return documentRequests[documentRequests.length - 1]
      },
      lastMessageSrc() {
        if (!this.lastMessage)
          return require('../assets/gray-env.png')

        switch (this.lastMessage.status) {
          case 'pending':
            return require('../assets/yellow-env.png')
          case 'failed':
            return require('../assets/red-env.png')
          case 'delivered':
          case 'opened':
            return require('../assets/green-env.png')
        }
      },
      lastMessageAlt() {
        if (!this.lastMessage)
          return this.$t('contacts.no_sent_message')

        const d = this.formatDate(this.lastMessage.created_at)
        const t = new Date(this.lastMessage.created_at).toLocaleTimeString(this.locale, { timeStyle: 'short' })
        switch (this.lastMessage.status) {
          case 'pending':
            return this.$t('contacts.sending_message', { date: d, time: t })
          case 'failed':
            return this.$t('contacts.message_not_sent', { date: d, time: t })
          case 'delivered':
          case 'opened':
            return this.$t('contacts.message_sent', { date: d, time: t })
        }
      },
      contactPageUrl() {
        return window.location.origin + this.$router.resolve({ name: 'public_contact_page', params: { uuid: this.contact.uuid } }).href
      },
    },
    methods: {
      loadFolders() {
        this.statStore.getFolders({ 'filters[contact_id]': this.$props.id }).then(() => {
          this.loading = false
        })
      },
      loadContact() {
        this.store.getContact(this.$props.id)
        this.statStore.getContacts({ 'filters[id]': this.$props.id })
      },
      updateContact(event) {
        this.store.updateContact(this.contact.id, new FormData(event.target)).then(() => {
          this.store.sendMetrics({name: 'update_contact', params: this.$route.params, path: this.$route.fullPath})
          this.loadContact()
          this.$refs.contactForm.close()
        })
      },
      removeConnectedTag(tag) {
        this.store.removeConnectedTag(tag.id, { contact_id: this.contact.id }).then(() => {
          this.store.sendMetrics({name: 'remove_tag_from_contact', params: this.$route.params, path: this.$route.fullPath})
          this.loadContact()
        })
      },
    }
  }
</script>

<style lang='sass'>
  @import '../styles/checklists'
  @import '../styles/contact'
</style>
